import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { recoverIncompletePurchases } from '@/services/payments/jest-payments';

/**
 * Drains any Jest IAP purchases that didn't finalize (page crashed mid-grant,
 * network drop between beginPurchase and completePurchase, etc.).
 *
 * Per https://docs.jest.com/sdk/html5/payments the game must check for
 * incomplete purchases every time it starts. We can't do that at boot time
 * before React mounts — auth is intentionally not persisted across reloads
 * (Jest owns identity), so the backend grant call would 401. Instead we run
 * as soon as both the SDK and an access token are available, which means:
 *
 *   - after the player completes the fresh /api/auth/jest sign-in on this
 *     load, accessToken transitions null → set and the hook fires once
 *
 * The runOnce ref guarantees we don't double-run if the auth store re-emits.
 */
export function useJestPurchaseRecovery(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const runOnce = useRef(false);

  useEffect(() => {
    if (runOnce.current) return;
    if (!isHydrated) return; // wait for zustand persist rehydration
    if (typeof window === 'undefined') return;

    const sdk = window.JestSDK;
    if (!sdk) {
      console.info('[jest-client] Purchase recovery: SDK not loaded, skipping');
      return;
    }
    if (!accessToken) {
      console.info('[jest-client] Purchase recovery: no auth token yet, deferring until sign-in');
      return;
    }

    runOnce.current = true;
    console.info('[jest-client] Purchase recovery: checking for incomplete purchases…');

    recoverIncompletePurchases(sdk, accessToken)
      .then((recovered) => {
        if (recovered > 0) {
          console.info(`[jest-client] Purchase recovery: completed ${recovered} purchase(s)`);
        } else {
          console.info('[jest-client] Purchase recovery: none pending');
        }
      })
      .catch((err) => {
        // Don't surface to the user — Jest will re-issue the same incomplete
        // purchases on the next load and we'll retry then.
        console.error('[jest-client] Purchase recovery failed:', err);
        runOnce.current = false; // allow another attempt next mount
      });
  }, [accessToken, isHydrated]);
}
